import DoubleSidebar from '@seller-hermes/features/ui-v2/double-sidebar';
import Popover from '@seller-hermes/features/ui-v2/Popover';
import ToggleGroup from '@seller-hermes/features/ui-v2/ToggleGroup';
import Tooltip from '@seller-hermes/features/ui-v2/Tooltip';
import { cn } from '@seller-hermes/utils/cn';
import {
  ChevronDown,
  CreditCard,
  Home,
  LayoutGrid,
  Truck,
  type LucideIcon,
} from 'lucide-react';
import * as React from 'react';
import type { PortalContextStatus, PortalProfile } from './portal.types';

interface ContextOption {
  id: string;
  profile: PortalProfile;
  status: PortalContextStatus;
  accountName: string;
  name: string;
  isHomologation?: boolean;
}

interface ContextSwitcherProps {
  activeProfile: PortalProfile;
  activeContextId: string | null;
  isHomologation: boolean;
  isMultiEnv: boolean;
  contexts: ContextOption[];
  hasHomologationContext: boolean;
  portalUrl: string;
  onProfileChange: (profile: PortalProfile) => void;
  onContextChange: (id: string) => void;
  onHomologationToggle: (enabled: boolean) => void;
}

type ProfileConfig = {
  label: string;
  Icon: LucideIcon;
  activeClass: string;
  activeIconClass: string;
};

const PROFILE_ORDER: PortalProfile[] = ['LOG', 'GO', 'PAY'];

const PROFILE_CONFIG: Record<PortalProfile, ProfileConfig> = {
  // `bg-white`/`text-*-700` são feitos pra pílula ativa CLARA. No tema escuro
  // isso vira um retângulo branco chapado com texto escuro — some do painel. As
  // variantes `dark:` deixam a pílula ativa seguir o tema (branco translúcido
  // sobre a trilha escura) mantendo o destaque, e clareiam o texto/ícone.
  LOG: {
    label: 'LOG',
    Icon: Home,
    activeClass:
      'data-[state=on]:text-emerald-700 data-[state=on]:bg-white dark:data-[state=on]:text-emerald-300 dark:data-[state=on]:bg-white/10',
    activeIconClass: 'text-emerald-700 dark:text-emerald-300',
  },
  GO: {
    label: 'GO',
    Icon: Truck,
    activeClass:
      'data-[state=on]:text-violet-700 data-[state=on]:bg-white dark:data-[state=on]:text-violet-300 dark:data-[state=on]:bg-white/10',
    activeIconClass: 'text-violet-700 dark:text-violet-300',
  },
  PAY: {
    label: 'PAY',
    Icon: CreditCard,
    activeClass:
      'data-[state=on]:text-blue-700 data-[state=on]:bg-white dark:data-[state=on]:text-blue-300 dark:data-[state=on]:bg-white/10',
    activeIconClass: 'text-blue-700 dark:text-blue-300',
  },
};

const getPortalSelectionUrl = (portalUrl: string) => {
  if (!portalUrl) return '';

  try {
    const url = new URL(portalUrl, window.location.origin);
    url.searchParams.set('select_context', 'true');
    return url.toString();
  } catch {
    const separator = portalUrl.includes('?') ? '&' : '?';
    return `${portalUrl}${separator}select_context=true`;
  }
};

export function ContextSwitcher({
  activeProfile,
  activeContextId,
  isHomologation,
  isMultiEnv,
  contexts,
  hasHomologationContext,
  portalUrl,
  onProfileChange,
  onContextChange,
  onHomologationToggle,
}: ContextSwitcherProps) {
  const [envPickerOpen, setEnvPickerOpen] = React.useState(false);

  if (!contexts?.length) return null;

  const { Icon: ActiveIcon, activeIconClass } = PROFILE_CONFIG[activeProfile];
  const activeContext = contexts.find(
    (c) => (isMultiEnv ? c.id === activeContextId : c.profile === activeProfile),
  );
  const portalSelectionUrl = getPortalSelectionUrl(portalUrl);

  return (
    <Tooltip.Provider>
      {/* Icon-only badge: visible when sidebar is collapsed */}
      <div className="hidden group-data-[collapsible=icon]:flex justify-center px-2 py-2">
        <Tooltip>
          <Tooltip.Trigger asChild>
            <div
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-md bg-white shadow-sm',
                isHomologation && 'ring-2 ring-amber-400',
              )}
            >
              <ActiveIcon className={cn('h-4 w-4', activeIconClass)} />
            </div>
          </Tooltip.Trigger>
          <Tooltip.Content side="right">
            {activeProfile}
            {isHomologation ? ' · HML' : ''}
          </Tooltip.Content>
        </Tooltip>
      </div>

      {/* Full content: hidden when sidebar is collapsed */}
      <div className="flex flex-col gap-1 px-2 py-2 group-data-[collapsible=icon]:hidden">
        <DoubleSidebar.GroupLabel>TRABALHANDO EM</DoubleSidebar.GroupLabel>

        {isMultiEnv ? (
          // Multi-env mode: dropdown trigger showing only the active context
          <Popover open={envPickerOpen} onOpenChange={setEnvPickerOpen}>
            <Popover.Trigger asChild>
              <button
                type="button"
                className="bg-muted hover:bg-muted-foreground/10 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors"
              >
                <ActiveIcon className={cn('h-3.5 w-3.5 shrink-0', activeIconClass)} />
                <span className="truncate font-medium">
                  {activeContext?.name ?? ''}
                </span>
                {activeContext?.isHomologation && (
                  <span className="shrink-0 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-700">
                    HML
                  </span>
                )}
                <ChevronDown
                  className={cn(
                    'ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                    envPickerOpen && 'rotate-180',
                  )}
                />
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                align="start"
                side="bottom"
                sideOffset={4}
                className="p-1"
              >
                {[...contexts].sort((a, b) => PROFILE_ORDER.indexOf(a.profile) - PROFILE_ORDER.indexOf(b.profile)).map(({ id, profile, name, status, isHomologation: isHml }) => {
                  const { Icon, activeIconClass: iconClass } = PROFILE_CONFIG[profile];
                  const isActive = id === activeContextId;

                  if (status === 'unavailable') {
                    return (
                      <Tooltip key={id}>
                        <Tooltip.Trigger asChild>
                          <span className="flex cursor-not-allowed items-center gap-2 rounded-sm px-2 py-1.5 opacity-50">
                            <Icon className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate text-xs">{name}</span>
                          </span>
                        </Tooltip.Trigger>
                        <Tooltip.Content>Ambiente indisponivel</Tooltip.Content>
                      </Tooltip>
                    );
                  }

                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => {
                        if (!isActive) onContextChange(id);
                        setEnvPickerOpen(false);
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition-colors',
                        isActive
                          ? 'bg-muted font-medium'
                          : 'hover:bg-muted cursor-pointer',
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-3.5 w-3.5 shrink-0',
                          isActive ? iconClass : 'text-muted-foreground',
                        )}
                      />
                      <span className="truncate">{name}</span>
                      {isHml && (
                        <span className="ml-auto shrink-0 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-700">
                          HML
                        </span>
                      )}
                    </button>
                  );
                })}
              </Popover.Content>
            </Popover.Portal>
          </Popover>
        ) : (
          // Normal mode: toggle group (current behavior)
          (() => {
            const isInteractive = contexts.length >= 2;
            return (
              <ToggleGroup
                type="single"
                variant="outline"
                value={activeProfile}
                onValueChange={(value) => {
                  if (isInteractive && value && value !== activeProfile) {
                    onProfileChange(value as PortalProfile);
                  }
                }}
                className={cn(
                  'bg-muted w-full',
                  !isInteractive && 'pointer-events-none',
                )}
              >
                {[...contexts].sort((a, b) => PROFILE_ORDER.indexOf(a.profile) - PROFILE_ORDER.indexOf(b.profile)).map(({ profile, status }) => {
                  const { label, Icon, activeClass } = PROFILE_CONFIG[profile];

                  if (status === 'unavailable') {
                    return (
                      <Tooltip key={profile}>
                        <Tooltip.Trigger asChild>
                          <span>
                            <ToggleGroup.Item
                              value={profile}
                              disabled
                              className={cn(activeClass)}
                            >
                              <Icon className="h-4 w-4 shrink-0" />
                              <span className="ml-1 text-xs">{label}</span>
                            </ToggleGroup.Item>
                          </span>
                        </Tooltip.Trigger>
                        <Tooltip.Content>Perfil indisponivel</Tooltip.Content>
                      </Tooltip>
                    );
                  }

                  return (
                    <ToggleGroup.Item
                      key={profile}
                      value={profile}
                      className={cn(activeClass)}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="ml-1 text-xs">{label}</span>
                    </ToggleGroup.Item>
                  );
                })}
              </ToggleGroup>
            );
          })()
        )}

        <div className="flex items-center justify-between gap-1 px-1">
          <span className="text-muted-foreground truncate text-xs">
            {activeContext?.accountName ?? ''}
          </span>
          <a
            href={portalSelectionUrl}
            className="text-muted-foreground hover:text-foreground flex shrink-0 items-center gap-1 text-xs"
          >
            portal
            <LayoutGrid className="h-3 w-3" />
          </a>
        </div>

        {hasHomologationContext && (
          <div className="flex items-center gap-2 px-1 pt-1">
            <button
              role="switch"
              aria-checked={isHomologation}
              onClick={() => onHomologationToggle(!isHomologation)}
              className={cn(
                'focus-visible:ring-ring relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                isHomologation ? 'bg-amber-400' : 'bg-input',
              )}
            >
              <span
                className={cn(
                  'pointer-events-none inline-block h-4 w-4 translate-y-0.5 rounded-full bg-white shadow-sm ring-0 transition-transform',
                  isHomologation ? 'translate-x-4' : 'translate-x-0.5',
                )}
              />
            </button>
            <span
              className={cn(
                'text-xs',
                isHomologation
                  ? 'font-bold text-amber-700'
                  : 'text-muted-foreground font-normal',
              )}
            >
              {isHomologation ? 'HOMOLOGACAO' : 'Homologacao'}
            </span>
          </div>
        )}
      </div>
    </Tooltip.Provider>
  );
}
